import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CollapsibleBoxComponent from '../../../components/CollapsibleBox/CollapsibleBoxComponent';
import TextFieldComponent from '../../../components/TextField/TextFieldComponent';
import DropdownComponent from '../../../components/DropDown/DropDownComponent';
import LoadingDots from '../../../components/LoadingDots/LoadingDots';
import { withMinDuration } from '../../../utils/withMinDuration';
import propertyTemplate from '../../../json/components/subscreens/PropertyDetailsTemplate.json';
import useValidationRegistry from '../useValidationRegistry';

const componentMap = {
  'text-field': TextFieldComponent,
  dropdown: DropdownComponent,
};

const cloneTemplate = (template = propertyTemplate) =>
  JSON.parse(JSON.stringify(template));

const getFreshProperty = () => cloneTemplate(propertyTemplate);

const formatNumber = (value) => {
  if (value === '' || value == null) return '';
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return String(Math.round(numeric));
  return String(value);
};

const buildAddressTitle = (property) => {
  const address = String(property?.streetAddress?.value || '').trim();
  return address || 'New property';
};

const resolvePropertyTitle = (values, fallbackTitle = 'New property') => {
  const title = buildAddressTitle(values);
  return title === 'New property' ? fallbackTitle : title;
};

const getPropertyAddress = (entry) =>
  String(
    entry?.Fulladdress || entry?.FullAddress || entry?.fullAddress || ''
  ).trim();

const normalizeAddressKey = (address) =>
  String(address || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const createEmptyApiRecord = () => ({
  building: null,
  homeContents: null,
});

const mergePropertyRecord = ({
  existingProperty = null,
  entry,
  entryType,
  index,
}) => {
  const address = getPropertyAddress(entry);
  const values = existingProperty
    ? cloneTemplate(existingProperty.values)
    : getFreshProperty();
  const apiRecord = existingProperty?.apiRecord
    ? { ...createEmptyApiRecord(), ...existingProperty.apiRecord }
    : createEmptyApiRecord();

  if (entryType === 'building') {
    values.buildingValue.value = formatNumber(entry?.BuildingValue);
    values.geysers.value = entry?.IncludeGeyser ? '1' : '';
    apiRecord.building = entry;
  }

  if (entryType === 'homeContents') {
    values.homeContentsValue.value = formatNumber(entry?.ContentsValue);
    apiRecord.homeContents = entry;
  }

  values.streetAddress.value = address || values.streetAddress.value || '';

  return {
    id:
      existingProperty?.id ||
      `${entryType}-${index}${address ? `-${normalizeAddressKey(address)}` : ''}`,
    title:
      address ||
      existingProperty?.title ||
      (entryType === 'building'
        ? `Building ${index + 1}`
        : `Home contents ${index + 1}`),
    values,
    originalValues: cloneTemplate(values),
    apiRecord,
  };
};

const mapPropertyResponseToSavedProperties = (response) => {
  if (!response || typeof response !== 'object') return [];

  const buildingValues = Array.isArray(response.BuildingValues)
    ? response.BuildingValues
    : [];
  const homeContentsValues = Array.isArray(response.HomeContentsValues)
    ? response.HomeContentsValues
    : [];

  const savedProperties = [];
  const propertyByAddress = new Map();

  buildingValues.forEach((entry, index) => {
    const address = getPropertyAddress(entry);
    const addressKey = normalizeAddressKey(address);
    const existingProperty = addressKey
      ? propertyByAddress.get(addressKey)
      : null;
    const nextProperty = mergePropertyRecord({
      existingProperty,
      entry,
      entryType: 'building',
      index,
    });

    if (addressKey) {
      propertyByAddress.set(addressKey, nextProperty);
    } else {
      savedProperties.push(nextProperty);
    }
  });

  homeContentsValues.forEach((entry, index) => {
    const address = getPropertyAddress(entry);
    const addressKey = normalizeAddressKey(address);
    const existingProperty = addressKey
      ? propertyByAddress.get(addressKey)
      : null;
    const nextProperty = mergePropertyRecord({
      existingProperty,
      entry,
      entryType: 'homeContents',
      index,
    });

    if (addressKey) {
      propertyByAddress.set(addressKey, nextProperty);
    } else {
      savedProperties.push(nextProperty);
    }
  });

  return [...savedProperties, ...Array.from(propertyByAddress.values())];
};

// Pre-live: property management (add / edit / reset) is temporarily disabled so
// the account page is view-only. Flip to true to restore these actions post-live.
const ALLOW_PROPERTY_EDITING = false;

const PropertyDetailsContent = ({
  isMobile,
  savedPropertiesData = null,
  onResetProperties,
}) => {
  const { registerValidation, validateAll } = useValidationRegistry();
  const [savedProperties, setSavedProperties] = useState(() =>
    mapPropertyResponseToSavedProperties(savedPropertiesData)
  );
  const [showNewProperty, setShowNewProperty] = useState(false);
  const [newProperty, setNewProperty] = useState(getFreshProperty());
  const [isSaving, setIsSaving] = useState(false);
  const [resettingPropertyIndices, setResettingPropertyIndices] = useState(
    () => new Set()
  );

  useEffect(() => {
    setSavedProperties(
      mapPropertyResponseToSavedProperties(savedPropertiesData)
    );
  }, [savedPropertiesData]);

  const newPropertyTitle = buildAddressTitle(newProperty);

  const handleChange = (name, value) => {
    setNewProperty((prev) => {
      if (!prev[name]) return prev;

      return {
        ...prev,
        [name]: {
          ...prev[name],
          value,
        },
      };
    });
  };

  const handleReset = () => {
    setNewProperty(getFreshProperty());
  };

  const handleSave = async () => {
    if (!validateAll()) {
      return;
    }

    setIsSaving(true);
    try {
      await withMinDuration(
        (async () => {
          const savedValues = cloneTemplate(newProperty);
          setSavedProperties((prev) => [
            ...prev,
            {
              id: Date.now(),
              title: newPropertyTitle,
              values: savedValues,
              originalValues: cloneTemplate(savedValues),
              apiRecord: null,
            },
          ]);
          setShowNewProperty(false);
          setNewProperty(getFreshProperty());
        })()
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddProperty = () => {
    setShowNewProperty(true);
    setNewProperty(getFreshProperty());
  };

  const handleSavedPropertyChange = (index, name, value) => {
    setSavedProperties((prev) =>
      prev.map((property, currentIndex) => {
        if (currentIndex !== index) return property;
        if (!property?.values?.[name]) return property;

        const nextValues = {
          ...property.values,
          [name]: {
            ...property.values[name],
            value,
          },
        };

        return {
          ...property,
          title: resolvePropertyTitle(nextValues, property.title),
          values: nextValues,
        };
      })
    );
  };

  const handleResetSavedProperty = async (index) => {
    setResettingPropertyIndices((prev) => new Set(prev).add(index));
    try {
      await withMinDuration(
        (async () => {
          if (onResetProperties) {
            try {
              await onResetProperties?.(undefined, {
                showLoader: false,
                forceRefresh: true,
              });
              return;
            } catch (error) {
              // ignore
            }
          }

          setSavedProperties((prev) =>
            prev.map((property, currentIndex) => {
              if (currentIndex !== index) return property;

              const originalValues = property?.originalValues
                ? cloneTemplate(property.originalValues)
                : getFreshProperty();

              return {
                ...property,
                title: resolvePropertyTitle(originalValues, property.title),
                values: originalValues,
              };
            })
          );
        })()
      );
    } finally {
      setResettingPropertyIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const renderPropertyFields = (
    property,
    onChange,
    registerFieldValidation = null,
    isReadOnly = false
  ) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 416px))',
        columnGap: isMobile ? 0 : '32px',
        rowGap: isMobile ? 4 : '16px',
        justifyContent: 'center',
        alignItems: 'start',
      }}
    >
      {Object.entries(property || {}).map(([name, question]) => {
        if (!question || typeof question !== 'object' || !question.type) {
          return null;
        }

        const Component = componentMap[question.type];
        if (!Component) return null;
        const isFullWidth = question.customSize === 'accounts-full';

        return (
          <Box
            key={name}
            sx={{
              width: '100%',
              gridColumn: !isMobile && isFullWidth ? '1 / -1' : 'auto',
            }}
          >
            <Component
              name={name}
              question={question}
              value={question.value}
              values={property}
              onChange={onChange}
              registerValidation={registerFieldValidation}
              isMobile={isMobile}
              layoutVariant="account"
              readOnly={isReadOnly}
            />
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box>
      {savedProperties.map((property, index) => (
        <CollapsibleBoxComponent
          key={property?.id ?? `${property.title}-${index}`}
          title={property.title || `Property ${index + 1}`}
          isMobile={isMobile}
          defaultExpanded={false}
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderPropertyFields(
              property.values,
              (fieldName, value) =>
                handleSavedPropertyChange(index, fieldName, value),
              null,
              true
            )}

            {ALLOW_PROPERTY_EDITING ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Button
                  color="inherit"
                  onClick={() => handleResetSavedProperty(index)}
                  disabled={resettingPropertyIndices.has(index)}
                  sx={{
                    backgroundColor: '#E2E5EA',
                    px: 4,
                    py: 1.5,
                    borderRadius: '20px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: isMobile ? '0.8125rem' : '1.25rem',
                    color: '#1C2F3A',
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: '120px' },
                  }}
                >
                  {resettingPropertyIndices.has(index) ? (
                    <LoadingDots />
                  ) : (
                    'Reset'
                  )}
                </Button>
              </Box>
            ) : null}
          </Box>
        </CollapsibleBoxComponent>
      ))}

      {ALLOW_PROPERTY_EDITING && !showNewProperty ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            onClick={handleAddProperty}
            sx={{
              height: '40px',
              backgroundColor: '#FF0074',
              borderRadius: '16px',
              px: 2,
              py: 1,
              gap: '12px',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: '1rem',
              letterSpacing: '-0.002em',
              fontFamily: 'BInter, Inter, sans-serif',
              textTransform: 'none',
              boxShadow: 'none',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#e10068',
              },
            }}
          >
            Add property
            <AddIcon sx={{ fontSize: '1.1rem' }} />
          </Button>
        </Box>
      ) : null}

      {ALLOW_PROPERTY_EDITING && showNewProperty ? (
        <CollapsibleBoxComponent
          title="New property"
          isMobile={isMobile}
          defaultExpanded
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderPropertyFields(
              newProperty,
              handleChange,
              registerValidation
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mt: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
              }}
            >
              <Button
                color="inherit"
                onClick={() => {
                  handleReset();
                  setShowNewProperty(false);
                }}
                sx={{
                  backgroundColor: '#E2E5EA',
                  px: 4,
                  py: 1.5,
                  borderRadius: '20px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: isMobile ? '0.8125rem' : '1.25rem',
                  color: '#1C2F3A',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  backgroundColor: '#C7CA00',
                  px: 4,
                  py: 1.5,
                  borderRadius: '20px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: isMobile ? '0.8125rem' : '1.25rem',
                  color: '#1C2F3A',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: '205px' },
                  '&:hover': {
                    backgroundColor: '#b7ba00',
                  },
                }}
              >
                {isSaving ? <LoadingDots /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CollapsibleBoxComponent>
      ) : null}
    </Box>
  );
};

export default PropertyDetailsContent;
